/**
 * Session-stash helpers for the ENTERPRISE-admin emulation flow.
 *
 * Parallel to src/lib/impersonation.ts but a SEPARATE, versioned key
 * (`__buildit_enterprise_emulation_v1`) so the two reconcile loops never
 * fight over one slot and the shape checks stay independent (spec §6.6).
 *
 * The stash holds the admin's tokens (to restore on exit) plus the
 * audit_id and emulation metadata for banner rendering, including the
 * enterprise `partner_id` / `partner_name` and `mode:'enterprise_admin'`.
 *
 * SECURITY: the stash holds the admin's access+refresh tokens and MUST
 * live in `sessionStorage` ONLY — never `localStorage` (spec §6.6). The
 * emulated session is full read-write, uncapped, and up to 12h, so the
 * token-theft / XSS blast radius is bounded by tab-close eviction.
 */

const STASH_KEY = '__buildit_enterprise_emulation_v1';

export interface EnterpriseEmulationStash {
  audit_id: string;
  admin_access_token: string;
  admin_refresh_token: string;
  admin_user_id: string;
  admin_email: string;
  target_user_id: string;
  target_email: string;
  partner_id: string;
  partner_name: string;
  started_at: string;
  read_only: boolean;
  reason: string;
  mode: 'enterprise_admin';
}

export function readEnterpriseEmulationStash(): EnterpriseEmulationStash | null {
  try {
    const raw = sessionStorage.getItem(STASH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as EnterpriseEmulationStash;
    // Minimal shape check — anything missing means the stash is from a
    // different release or has been tampered with; treat as absent.
    if (
      !parsed.audit_id ||
      !parsed.admin_access_token ||
      !parsed.admin_refresh_token ||
      !parsed.target_user_id ||
      !parsed.partner_id ||
      parsed.mode !== 'enterprise_admin'
    ) {
      sessionStorage.removeItem(STASH_KEY);
      return null;
    }
    return parsed;
  } catch {
    sessionStorage.removeItem(STASH_KEY);
    return null;
  }
}

export function writeEnterpriseEmulationStash(stash: EnterpriseEmulationStash): void {
  // sessionStorage ONLY — see security note above. Never localStorage.
  sessionStorage.setItem(STASH_KEY, JSON.stringify(stash));
}

export function clearEnterpriseEmulationStash(): void {
  sessionStorage.removeItem(STASH_KEY);
}
